/**
 * Conviction Trend Chart — plots Opportunity Score / Confidence / Risk Score
 * across an opportunity's score history. Higher score & confidence = better
 * conviction. Higher risk = worse — the chart legend uses labels & icons so we
 * never rely on colour alone. Range chips filter the underlying data.
 */
import { useMemo, useState } from "react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ReferenceDot, Legend,
} from "recharts";
import { TrendingUp, Shield, Gauge } from "lucide-react";

const RANGES = [
  { key: "1D",  label: "1D",  ms: 24 * 3600e3 },
  { key: "1W",  label: "1W",  ms: 7 * 24 * 3600e3 },
  { key: "1M",  label: "1M",  ms: 30 * 24 * 3600e3 },
  { key: "3M",  label: "3M",  ms: 90 * 24 * 3600e3 },
  { key: "ALL", label: "All", ms: null },
];

const SERIES = [
  { key: "score",      label: "Opp Score",  color: "#3b82f6", direction: "higher_is_better", icon: TrendingUp },
  { key: "confidence", label: "Confidence", color: "#eab308", direction: "higher_is_better", icon: Gauge },
  { key: "risk_score", label: "Risk",       color: "#ef4444", direction: "higher_is_worse",  icon: Shield },
];

const MARKER_KEYS = new Set([
  "verdict_change", "FULL_ANALYSIS_COMPLETED", "SUPERSEDED",
  "PROPOSAL_CREATED", "PAPER_POSITION_OPEN", "CLOSED_WINNER",
  "CLOSED_LOSER", "CLOSED_BREAKEVEN",
]);

function fmtTick(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch { return iso; }
}
function fmtTs(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function TooltipBox({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="border border-border/70 bg-background/95 p-2 text-[10px] mono">
      <div className="text-muted-foreground mb-1">{fmtTs(label)}</div>
      {payload.map(p => (
        <div key={p.dataKey} className="flex items-baseline gap-2">
          <span style={{ color: p.color }}>■</span>
          <span>{p.name}</span>
          <span className="ml-auto">{p.value ?? "—"}</span>
        </div>
      ))}
    </div>
  );
}

export default function ConvictionTrendChart({
  history = [],
  lifecycleEvents = [],
}) {
  const [range, setRange] = useState("ALL");
  const [enabled, setEnabled] = useState(() => Object.fromEntries(SERIES.map(s => [s.key, true])));

  const filtered = useMemo(() => {
    const cfg = RANGES.find(r => r.key === range);
    const sorted = [...(history || [])].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
    if (!cfg?.ms) return sorted;
    const cutoff = Date.now() - cfg.ms;
    return sorted.filter(h => new Date(h.timestamp).getTime() >= cutoff);
  }, [history, range]);

  const data = useMemo(() => filtered.map(h => ({
    t: h.timestamp,
    score: h.score ?? null,
    confidence: h.confidence ?? null,
    risk_score: h.risk_score ?? null,
    verdict: h.verdict,
    reasons: h.change_reasons,
  })), [filtered]);

  const markers = useMemo(() => {
    const outByT = new Map();
    (lifecycleEvents || []).forEach(e => {
      if (!MARKER_KEYS.has(e.new_state)) return;
      const t = e.timestamp;
      if (!outByT.has(t)) outByT.set(t, []);
      outByT.get(t).push(e);
    });
    // Snap marker to nearest data point timestamp so it renders on the axis
    return [...outByT.entries()].map(([t, evts]) => {
      let closest = null;
      let bestDelta = Infinity;
      for (const d of data) {
        const delta = Math.abs(new Date(d.t).getTime() - new Date(t).getTime());
        if (delta < bestDelta) { bestDelta = delta; closest = d; }
      }
      return closest ? { t: closest.t, evts } : null;
    }).filter(Boolean);
  }, [lifecycleEvents, data]);

  if (data.length === 0) {
    return (
      <div className="border border-border/40 p-3" data-testid="conviction-trend-empty">
        <div className="label-cap mb-1">Conviction Trend</div>
        <p className="text-[11px] text-muted-foreground italic">
          Not enough score history yet — snapshots are recorded when the AI conviction changes materially.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="conviction-trend-chart">
      <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
        <div className="label-cap">Conviction Trend</div>
        <div className="flex items-center gap-1" role="group" aria-label="Chart range">
          {RANGES.map(r => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              aria-pressed={range === r.key}
              data-testid={`conviction-range-${r.key}`}
              className={`mono uppercase text-[9px] px-2 h-6 border ${range === r.key ? "border-signal-blue text-signal-blue" : "border-border/40 text-muted-foreground hover:border-signal-blue/50"}`}
            >{r.label}</button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-2">
        {SERIES.map(s => {
          const Icon = s.icon;
          const on = enabled[s.key];
          return (
            <button
              key={s.key}
              onClick={() => setEnabled(prev => ({ ...prev, [s.key]: !prev[s.key] }))}
              aria-pressed={on}
              data-testid={`conviction-toggle-${s.key}`}
              className={`flex items-center gap-1.5 mono uppercase text-[10px] px-2 h-6 border ${on ? "border-current" : "border-border/40 text-muted-foreground opacity-60"}`}
              style={on ? { color: s.color } : undefined}
              title={s.direction === "higher_is_worse"
                ? `${s.label} — higher means MORE risk`
                : `${s.label} — higher means STRONGER conviction`}
            >
              <Icon size={10} />
              <span>{s.label}</span>
              <span className="text-[8px] opacity-70">
                {s.direction === "higher_is_worse" ? "↑=risk" : "↑=strong"}
              </span>
            </button>
          );
        })}
      </div>

      <div style={{ width: "100%", height: 220 }}>
        <ResponsiveContainer width="100%" height="100%" minWidth={320} minHeight={200} debounce={50}>
          <LineChart data={data} margin={{ top: 6, right: 8, left: -20, bottom: 0 }}>
            <CartesianGrid strokeOpacity={0.15} vertical={false} />
            <XAxis
              dataKey="t"
              tickFormatter={fmtTick}
              tick={{ fontSize: 9, fill: "currentColor" }}
              stroke="currentColor"
              strokeOpacity={0.4}
              minTickGap={40}
            />
            <YAxis
              domain={[0, 100]}
              tick={{ fontSize: 9, fill: "currentColor" }}
              stroke="currentColor"
              strokeOpacity={0.4}
              width={30}
            />
            <Tooltip content={<TooltipBox />} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            {SERIES.filter(s => enabled[s.key]).map(s => (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.label}
                stroke={s.color}
                strokeWidth={1.5}
                dot={{ r: 2, strokeWidth: 0, fill: s.color }}
                activeDot={{ r: 4 }}
                isAnimationActive={false}
                connectNulls
              />
            ))}
            {markers.map((m, i) => (
              <ReferenceDot
                key={`m-${i}`}
                x={m.t}
                y={98}
                r={4}
                fill="var(--signal-blue, #3b82f6)"
                stroke="var(--background)"
                strokeWidth={1}
                ifOverflow="extendDomain"
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="text-[10px] text-muted-foreground italic mt-1">
        Blue dots mark material lifecycle events (analysis completed, proposal created, position closed, etc.).
      </p>
    </div>
  );
}
